import { useState, useCallback, useEffect, useMemo } from 'react'
import {
  type DurationOption,
  type DurationPresetSettings,
  DEFAULT_DURATION_OPTIONS,
  DEFAULT_DURATION_PRESET_SETTINGS,
} from '../types/program-scheduling.types'

const STORAGE_KEY = 'exercise-toolbox-duration-presets'

/**
 * Hook for managing custom duration presets
 * Persists to localStorage for user preferences
 */
export function useDurationPresets() {
  const [settings, setSettings] = useState<DurationPresetSettings>(DEFAULT_DURATION_PRESET_SETTINGS)
  const [isLoaded, setIsLoaded] = useState(false)

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as DurationPresetSettings
        setSettings(parsed)
      }
    } catch (e) {
      console.error('Failed to load duration presets:', e)
    }
    setIsLoaded(true)
  }, [])

  // Save settings to localStorage whenever they change
  const saveSettings = useCallback((newSettings: DurationPresetSettings) => {
    setSettings(newSettings)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSettings))
    } catch (e) {
      console.error('Failed to save duration presets:', e)
    }
  }, [])

  // Get all visible duration options (defaults + custom, minus hidden)
  const visibleDurations = useMemo((): DurationOption[] => {
    const visible: DurationOption[] = []

    // Add non-hidden default options
    for (const opt of DEFAULT_DURATION_OPTIONS) {
      if (!settings.hiddenDefaults.includes(opt.id)) {
        visible.push(opt)
      }
    }

    // Add custom durations
    visible.push(...settings.customDurations)

    return visible
  }, [settings])

  // Add a new custom duration
  const addCustomDuration = useCallback((duration: Omit<DurationOption, 'id' | 'isDefault'>) => {
    const newDuration: DurationOption = {
      ...duration,
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      isDefault: false,
    }

    saveSettings({
      ...settings,
      customDurations: [...settings.customDurations, newDuration],
    })

    return newDuration.id
  }, [settings, saveSettings])

  // Update a custom duration
  const updateCustomDuration = useCallback((id: string, updates: Partial<Omit<DurationOption, 'id' | 'isDefault'>>) => {
    if (!id.startsWith('custom_')) {
      console.warn('Cannot update default duration options')
      return
    }

    saveSettings({
      ...settings,
      customDurations: settings.customDurations.map(d =>
        d.id === id ? { ...d, ...updates } : d
      ),
    })
  }, [settings, saveSettings])

  // Delete a custom duration
  const deleteCustomDuration = useCallback((id: string) => {
    if (!id.startsWith('custom_')) {
      console.warn('Cannot delete default duration options - use hideDefault instead')
      return
    }

    saveSettings({
      ...settings,
      customDurations: settings.customDurations.filter(d => d.id !== id),
    })
  }, [settings, saveSettings])

  // Hide a default duration option
  const hideDefault = useCallback((id: string) => {
    const isDefault = DEFAULT_DURATION_OPTIONS.some(d => d.id === id)
    if (!isDefault) {
      console.warn('Duration is not a default option')
      return
    }

    if (!settings.hiddenDefaults.includes(id)) {
      saveSettings({
        ...settings,
        hiddenDefaults: [...settings.hiddenDefaults, id],
      })
    }
  }, [settings, saveSettings])

  // Show a hidden default duration option
  const showDefault = useCallback((id: string) => {
    saveSettings({
      ...settings,
      hiddenDefaults: settings.hiddenDefaults.filter(hid => hid !== id),
    })
  }, [settings, saveSettings])

  // Check if a duration can be deleted (only custom ones)
  const canDelete = useCallback((id: string): boolean => {
    return id.startsWith('custom_')
  }, [])

  // Check if a duration can be hidden (only defaults)
  const canHide = useCallback((id: string): boolean => {
    return DEFAULT_DURATION_OPTIONS.some(d => d.id === id)
  }, [])

  // Get all hidden default options (for restore functionality)
  const hiddenDefaults = useMemo((): DurationOption[] => {
    return DEFAULT_DURATION_OPTIONS.filter(d => settings.hiddenDefaults.includes(d.id))
  }, [settings])

  // Reset all customizations
  const resetToDefaults = useCallback(() => {
    saveSettings(DEFAULT_DURATION_PRESET_SETTINGS)
  }, [saveSettings])

  // Get duration by ID (including custom)
  const getDurationById = useCallback((id: string): DurationOption | undefined => {
    const defaultOpt = DEFAULT_DURATION_OPTIONS.find(d => d.id === id)
    if (defaultOpt) return defaultOpt
    return settings.customDurations.find(d => d.id === id)
  }, [settings])

  return {
    // State
    visibleDurations,
    hiddenDefaults,
    customDurations: settings.customDurations,
    isLoaded,

    // Actions
    addCustomDuration,
    updateCustomDuration,
    deleteCustomDuration,
    hideDefault,
    showDefault,
    resetToDefaults,

    // Helpers
    canDelete,
    canHide,
    getDurationById,
  }
}
